import { getPlan, NotFound, Pick, type Plan } from '@trippath/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { useLive } from '../../app/live';
import { dayRange } from '../../lib/countryFacts';
import {
  age,
  areasOf,
  estimate,
  forget,
  keep,
  keepPlan,
  keptOf,
  OLD_DAYS,
  onCellular,
  RADIUS_KM,
  setKept,
  setSettings,
  settings as savedSettings,
  type Area,
  type Kept,
  type Settings,
} from '../../lib/offlineMaps';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; plan: Plan };

/** An area loading: how much of it is kept so far. */
interface Loading {
  done: number;
  all: number;
  bytes: number;
}

/** What the phone's storage holds, as the browser says it. */
interface Storage {
  usage: number;
  quota: number;
  persisted: boolean;
}

/** The radii one may keep round a stop. */
const RADII = [10, 20, 25, 30, 50];

/** Megabytes, whole: `22 MB`. */
export function megabytes(bytes: number, language: string): string {
  return `${Math.max(bytes > 0 ? 1 : 0, Math.round(bytes / 1_000_000)).toLocaleString(language)} MB`;
}

/**
 * A trip's maps kept on this phone (the `OfflineMaps` mockup): the route seen from afar and each
 * stop's town round it, so the map, its places and its roads work in flight mode and without a
 * SIM — each kept, loading, waiting or old; how they are kept, over Wi-Fi only and new stops by
 * themselves; and what the phone's storage holds.
 */
export function OfflineMaps() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const { version: changed } = useLive();
  const [state, setState] = useState<State>({ kind: 'loading' });
  useEffect(() => {
    let live = true;
    getPlan(id).then(
      (plan) => {
        if (live) setState({ kind: 'ready', plan });
      },
      (error: unknown) => {
        if (live) setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
    return () => {
      live = false;
    };
  }, [id, changed]);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}
        </p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }
  return <Areas plan={state.plan} />;
}

/** A trip's areas, how they are kept, and the phone's storage. */
function Areas({ plan }: { plan: Plan }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const id = plan.trip.id;
  const [kept, setKeptState] = useState<Record<string, Kept>>(() => keptOf(id));
  const [chosen, setChosen] = useState<Settings>(savedSettings);
  const [loading, setLoading] = useState<Record<string, Loading>>({});
  const [waiting, setWaiting] = useState<string[]>([]);
  const [changing, setChanging] = useState(false);
  const [storage, setStorage] = useState<Storage | null>(null);
  const [now] = useState(() => Date.now());
  // Stopped when the page is left.
  const left = useRef(false);
  // New stops asked for once.
  const auto = useRef(false);

  useEffect(() => {
    left.current = false;
    return () => {
      left.current = true;
    };
  }, []);

  const measure = () => {
    const where = navigator.storage as StorageManager | undefined;
    if (!where) return;
    Promise.all([where.estimate(), where.persisted()]).then(
      ([said, persisted]) => {
        setStorage({ usage: said.usage ?? 0, quota: said.quota ?? 0, persisted });
      },
      () => undefined,
    );
  };
  useEffect(measure, []);

  const remember = useCallback(
    (next: Record<string, Kept>) => {
      setKeptState(next);
      setKept(id, next);
    },
    [id],
  );
  // Areas kept one after another: on a phone's own network only when allowed.
  const load = useCallback(
    async (wanted: Area[]) => {
      if (chosen.wifiOnly && onCellular(navigator)) {
        setWaiting(wanted.map((area) => area.id));
        return;
      }
      setWaiting(wanted.map((area) => area.id));
      let all = { ...kept };
      for (const area of wanted) {
        if (left.current) return;
        setWaiting((was) => was.filter((one) => one !== area.id));
        setLoading((was) => ({ ...was, [area.id]: { done: 0, all: 1, bytes: 0 } }));
        const made = await keep(
          area,
          (done, bytes, count) => {
            setLoading((was) => ({ ...was, [area.id]: { done, all: count, bytes } }));
          },
          () => left.current,
        ).catch(() => null);
        setLoading((was) =>
          Object.fromEntries(Object.entries(was).filter(([one]) => one !== area.id)),
        );
        if (made) {
          all = { ...all, [area.id]: made };
          remember(all);
        }
      }
      await keepPlan(id);
      measure();
    },
    [chosen.wifiOnly, kept, remember, id],
  );

  // New stops kept by themselves, once some of the trip is: on the next Wi-Fi. One let go of is
  // remembered as such, and not new.
  useEffect(() => {
    if (!chosen.autoNew || auto.current) return;
    const had = keptOf(id);
    const wanted = areasOf(plan, chosen.radius).filter((area) => !(area.id in had));
    if (Object.keys(had).length > 0 && wanted.length > 0) {
      auto.current = true;
      void Promise.resolve().then(() => load(wanted));
    }
  }, [plan, chosen, id, load]);

  // Areas waiting for Wi-Fi go on when it comes.
  useEffect(() => {
    const connection = (navigator as Navigator & { connection?: EventTarget }).connection;
    if (waiting.length === 0 || !connection) return;
    const changed = () => {
      if (!onCellular(navigator)) {
        void load(areasOf(plan, chosen.radius).filter((area) => waiting.includes(area.id)));
      }
    };
    connection.addEventListener('change', changed);
    return () => {
      connection.removeEventListener('change', changed);
    };
  }, [waiting, plan, chosen, load]);

  const areas = areasOf(plan, chosen.radius);
  const sizeOf = (area: Area) => {
    const own = kept[area.id];
    return own?.at ? own.bytes : estimate(area);
  };
  const total = areas.reduce((all, area) => all + sizeOf(area), 0);
  const kepts = areas.filter((area) => kept[area.id]?.at);
  const old = kepts.filter((area) => age(kept[area.id] as Kept, now) >= OLD_DAYS);
  const missing = areas.filter((area) => !kept[area.id]?.at && loading[area.id] === undefined);
  const stillToLoad = missing.reduce((all, area) => all + estimate(area), 0);
  const mapsBytes = kepts.reduce((all, area) => all + (kept[area.id] as Kept).bytes, 0);

  const choose = (next: Settings) => {
    setChosen(next);
    setSettings(next);
  };
  const drop = (area: Area) => {
    const rest = { ...kept, [area.id]: { tiles: 0, bytes: 0, at: null } };
    remember(rest);
    void forget(
      area,
      areas.filter((one) => one.id !== area.id && rest[one.id]?.at),
    ).then(measure);
  };

  const statusOf = (area: Area) => {
    const going = loading[area.id];
    if (going) {
      return {
        tone: 'info',
        text: t('offline.loading', {
          done: megabytes(going.bytes, lang),
          all: megabytes(estimate(area), lang),
        }),
        going,
      };
    }
    if (waiting.includes(area.id))
      return { tone: 'plain', text: t('offline.waiting'), going: null };
    const own = kept[area.id];
    if (own?.at) {
      const days = age(own, now);
      return days >= OLD_DAYS
        ? { tone: 'warn', text: t('offline.old', { count: days }), going: null }
        : { tone: 'ok', text: t('offline.kept'), going: null };
    }
    return { tone: 'plain', text: t('offline.none'), going: null };
  };

  return (
    <div className="page offline-page">
      <header className="page-head">
        <h1>{t('offline.title')}</h1>
        <p className="lead">{t('offline.lead')}</p>
      </header>
      <div className="offline-layout">
        <section className="panel" aria-labelledby="offline-areas">
          <div className="offline-head">
            <h2 id="offline-areas" className="panel-title">
              {t('offline.areas', { trip: plan.trip.name })}
            </h2>
            <span className="quiet offline-small">
              {t('offline.total', { size: megabytes(total, lang) })}
            </span>
          </div>
          {areas.length === 0 && <p className="quiet">{t('offline.noPlaces')}</p>}
          <ul className="offline-areas">
            {areas.map((area) => {
              const status = statusOf(area);
              const placed = plan.stops.length;
              return (
                <li key={area.id}>
                  <div>
                    <b>{area.stop === null ? t('offline.route') : area.name}</b>
                    <small className="quiet">
                      {area.stop === null || area.days === null
                        ? t('offline.routeHow', { count: placed })
                        : t('offline.stopHow', {
                            days: dayRange(area.days[0], area.days[1], lang),
                            km: area.radiusKm,
                          })}
                    </small>
                    {status.going && (
                      <progress
                        className="offline-progress"
                        max={status.going.all}
                        value={status.going.done}
                        aria-label={t('offline.progress', {
                          name: area.name || t('offline.route'),
                        })}
                      />
                    )}
                  </div>
                  <span className="mono offline-size">{megabytes(sizeOf(area), lang)}</span>
                  <span className={`tag offline-tag-${status.tone}`}>{status.text}</span>
                  {changing && area.stop !== null && (
                    <div className="offline-radius">
                      <Pick
                        label={t('offline.radiusOf', { name: area.name })}
                        quiet
                        searchable={false}
                        options={RADII.map((km) => ({
                          value: String(km),
                          label: t('offline.km', { km }),
                        }))}
                        value={String(area.radiusKm)}
                        onChange={(km) => {
                          choose({
                            ...chosen,
                            radius: { ...chosen.radius, [area.id]: Number(km) },
                          });
                        }}
                      />
                    </div>
                  )}
                  {kept[area.id]?.at && (
                    <button
                      type="button"
                      className="button button-small button-quiet"
                      onClick={() => {
                        drop(area);
                      }}
                    >
                      {t('offline.remove', { name: area.name || t('offline.route') })}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="offline-actions">
            {missing.length > 0 && (
              <button
                type="button"
                className="button button-primary"
                onClick={() => {
                  void load(missing);
                }}
              >
                {t('offline.loadAll', { size: megabytes(stillToLoad, lang) })}
              </button>
            )}
            {old.length > 0 && (
              <button
                type="button"
                className="button"
                onClick={() => {
                  void load(old);
                }}
              >
                {t('offline.renew')}
              </button>
            )}
            <button
              type="button"
              className="button"
              aria-pressed={changing}
              onClick={() => {
                setChanging(!changing);
              }}
            >
              {t('offline.changeRadius')}
            </button>
          </div>
          {waiting.length > 0 && chosen.wifiOnly && onCellular(navigator) && (
            <p className="quiet offline-small" role="status">
              {t('offline.waitingWifi')}
            </p>
          )}
        </section>
        <div className="offline-side">
          <section className="panel" aria-labelledby="offline-settings">
            <h2 id="offline-settings" className="panel-title">
              {t('offline.settings')}
            </h2>
            <label className="offline-switch">
              <input
                type="checkbox"
                checked={chosen.wifiOnly}
                onChange={() => {
                  choose({ ...chosen, wifiOnly: !chosen.wifiOnly });
                }}
              />
              <span>
                <b>{t('offline.wifiOnly')}</b>
                <small className="quiet">{t('offline.wifiOnlyHow')}</small>
              </span>
            </label>
            <label className="offline-switch">
              <input
                type="checkbox"
                checked={chosen.autoNew}
                onChange={() => {
                  choose({ ...chosen, autoNew: !chosen.autoNew });
                }}
              />
              <span>
                <b>{t('offline.autoNew')}</b>
                <small className="quiet">{t('offline.autoNewHow')}</small>
              </span>
            </label>
            <p className="offline-small">
              <b>{t('offline.renewing')}</b> {t('offline.renewingHow', { count: OLD_DAYS })}
            </p>
          </section>
          <section className="panel" aria-labelledby="offline-storage">
            <h2 id="offline-storage" className="panel-title">
              {t('offline.storage')}
            </h2>
            {storage === null ? (
              <p className="quiet offline-small">{t('offline.noStorage')}</p>
            ) : (
              <>
                <dl className="offline-storage">
                  <div>
                    <dt>{t('offline.maps')}</dt>
                    <dd className="mono">{megabytes(mapsBytes, lang)}</dd>
                  </div>
                  <div>
                    <dt>{t('offline.documents')}</dt>
                    <dd className="mono">
                      {megabytes(Math.max(0, storage.usage - mapsBytes), lang)}
                    </dd>
                  </div>
                </dl>
                <p className="offline-small">
                  {t('offline.free', {
                    size: (Math.max(0, storage.quota - storage.usage) / 1e9).toLocaleString(
                      lang,
                      {
                        maximumFractionDigits: 1,
                      },
                    ),
                  })}{' '}
                  · {storage.persisted ? t('offline.persisted') : t('offline.notPersisted')}
                </p>
                {!storage.persisted && (
                  <button
                    type="button"
                    className="button button-small"
                    onClick={() => {
                      void navigator.storage.persist().then(measure);
                    }}
                  >
                    {t('offline.persist')}
                  </button>
                )}
                <p className="quiet offline-small">{t('offline.persistHow')}</p>
              </>
            )}
          </section>
        </div>
      </div>
      <p className="quiet offline-small">{t('offline.radiusUsual', { km: RADIUS_KM })}</p>
    </div>
  );
}
